import React from 'react';
import { isNodeSelection } from '@tiptap/react';
import { Editor } from '@tiptap/react';
import { useRichTextTiptapEditor } from 'apps/sigma2-ui/src/components/rich-text/tiptap/hooks/use-tiptap-editor';
import { RichTextChevronDownIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/chevron-down-icon';
import { RichTextListIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/list-icon';
import { richTextIsNodeInSchema } from 'apps/sigma2-ui/src/components/rich-text/tiptap/lib/tiptap-utils';
import { RichTextListButton } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui/list-button/list-button';
import { richTextCanToggleList } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui/list-button/list-button';
import { richTextIsListActive } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui/list-button/list-button';
import { richTextListOptions } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui/list-button/list-button';
import { RichTextListType } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui/list-button/list-button';
import { RichTextButtonProps } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';
import { RichTextButton } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';
import { RichTextDropdownMenu } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/dropdown-menu/dropdown-menu';
import { RichTextDropdownMenuTrigger } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/dropdown-menu/dropdown-menu';
import { RichTextDropdownMenuContent } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/dropdown-menu/dropdown-menu';
import { RichTextDropdownMenuGroup } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/dropdown-menu/dropdown-menu';
import { RichTextDropdownMenuItem } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/dropdown-menu/dropdown-menu';

export interface RichTextListDropdownMenuProps
  extends Omit<RichTextButtonProps, 'type'> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor;
  /**
   * The list types to display in the dropdown.
   */
  types?: RichTextListType[];
  /**
   * Whether the dropdown should be hidden when no list types are available
   * @default false
   */
  hideWhenUnavailable?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export function richTextCanToggleAnyList(
  editor: Editor | null,
  listTypes: RichTextListType[],
): boolean {
  if (!editor) return false;
  return listTypes.some((type) => richTextCanToggleList(editor, type));
}

export function richTextIsAnyListActive(
  editor: Editor | null,
  listTypes: RichTextListType[],
): boolean {
  if (!editor) return false;
  return listTypes.some((type) => richTextIsListActive(editor, type));
}

export function richTextGetFilteredListOptions(
  availableTypes: RichTextListType[],
): typeof richTextListOptions {
  return richTextListOptions.filter(
    (option) => !option.type || availableTypes.includes(option.type),
  );
}

export function richTextShouldShowListDropdown(params: {
  editor: Editor | null;
  listTypes: RichTextListType[];
  hideWhenUnavailable: boolean;
  listInSchema: boolean;
  canToggleAny: boolean;
}): boolean {
  const { editor, hideWhenUnavailable, listInSchema, canToggleAny } = params;

  if (!listInSchema || !editor) {
    return false;
  }

  if (hideWhenUnavailable) {
    if (isNodeSelection(editor.state.selection) || !canToggleAny) {
      return false;
    }
  }

  return true;
}

export function useRichTextListDropdownState(
  editor: Editor | null,
  availableTypes: RichTextListType[],
) {
  const [isOpen, setIsOpen] = React.useState(false);

  const listInSchema = availableTypes.some((type) =>
    richTextIsNodeInSchema(type, editor),
  );

  const filteredLists = React.useMemo(
    () => richTextGetFilteredListOptions(availableTypes),
    [availableTypes],
  );

  const canToggleAny = richTextCanToggleAnyList(editor, availableTypes);
  const isAnyActive = richTextIsAnyListActive(editor, availableTypes);

  const handleOpenChange = React.useCallback(
    (open: boolean, callback?: (isOpen: boolean) => void) => {
      setIsOpen(open);
      callback?.(open);
    },
    [],
  );

  return {
    isOpen,
    setIsOpen,
    listInSchema,
    filteredLists,
    canToggleAny,
    isAnyActive,
    handleOpenChange,
  };
}

export function useRichTextActiveListIcon(
  editor: Editor | null,
  filteredLists: typeof richTextListOptions,
) {
  return React.useCallback(() => {
    const activeOption = filteredLists.find((option) =>
      richTextIsListActive(editor, option.type),
    );

    return activeOption ? (
      <activeOption.icon className="tiptap-button-icon" />
    ) : (
      <RichTextListIcon className="tiptap-button-icon" />
    );
  }, [editor, filteredLists]);
}

export function RichTextListDropdownMenu({
  editor: providedEditor,
  types = ['bulletList', 'orderedList', 'taskList'],
  hideWhenUnavailable = false,
  onOpenChange,
  ...props
}: RichTextListDropdownMenuProps) {
  const editor = useRichTextTiptapEditor(providedEditor);

  const {
    isOpen,
    listInSchema,
    filteredLists,
    canToggleAny,
    isAnyActive,
    handleOpenChange,
  } = useRichTextListDropdownState(editor, types);

  const getActiveIcon = useRichTextActiveListIcon(editor, filteredLists);

  const show = React.useMemo(() => {
    return richTextShouldShowListDropdown({
      editor,
      listTypes: types,
      hideWhenUnavailable,
      listInSchema,
      canToggleAny,
    });
  }, [editor, types, hideWhenUnavailable, listInSchema, canToggleAny]);

  const handleOnOpenChange = React.useCallback(
    (open: boolean) => handleOpenChange(open, onOpenChange),
    [handleOpenChange, onOpenChange],
  );

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <RichTextDropdownMenu open={isOpen} onOpenChange={handleOnOpenChange}>
      <RichTextDropdownMenuTrigger asChild>
        <RichTextButton
          type="button"
          data-style="ghost"
          data-active-state={isAnyActive ? 'on' : 'off'}
          role="button"
          tabIndex={-1}
          aria-label="List options"
          tooltip="List"
          {...props}
        >
          {getActiveIcon()}
          <RichTextChevronDownIcon className="tiptap-button-dropdown-small" />
        </RichTextButton>
      </RichTextDropdownMenuTrigger>

      <RichTextDropdownMenuContent>
        <RichTextDropdownMenuGroup>
          {filteredLists.map((option) => (
            <RichTextDropdownMenuItem key={option.type} asChild>
              <RichTextListButton
                editor={editor}
                type={option.type}
                text={option.label}
                hideWhenUnavailable={hideWhenUnavailable}
                tooltip={''}
              />
            </RichTextDropdownMenuItem>
          ))}
        </RichTextDropdownMenuGroup>
      </RichTextDropdownMenuContent>
    </RichTextDropdownMenu>
  );
}

export default RichTextListDropdownMenu;
